import { useEffect, useState, useMemo, useCallback } from 'react';
import Button from 'components/v4/Button';
import AGGridBase from './AGGridBase';
import AGGridSearchBar from './AGGridSearchBar';
import { useLocation } from 'react-router-dom';
import CopyCellRenderer from './CustomCells/CopyCellRenderer';

// Use AGGridMain to render a majority of the new tables
const AGGridMain = ({
    addButtonAction,
    gridRef,
    tableData,
    addButtonWalkthroughId,
    addButtonText,
    masterDetail,
    isLoading,
    noSearchBar,
    refetch,
    isRowMaster,
    rowModelType,
    extraComponents,
    titleName,
    rowSelection,
    onRowClicked,
    onSelectionChanged,
    overlayNoRowsTemplate,
    csvHeaders = {}, // Add new prop for custom headers
    ...props
}) => {
    const [quickFilterText, setQuickFilterText] = useState('');

    const { pathname } = useLocation();
    // const gridApi = gridRef.current?.api; // gridApi can be derived directly from gridRef.current.api when needed

    useEffect(() => {
        // Ensure API is available before calling sizeColumnsToFit
        if (gridRef.current?.api) {
            gridRef.current.api.sizeColumnsToFit();
        }
    }, [tableData, gridRef]); // Rerun if tableData changes or gridRef is updated

    const defaultColDef = useMemo(() => {
        return {
            sortable: true,
            enableRowGroup: true,
            filter: true,
            resizable: true,
            suppressSizeToFit: true,
            flex: 1,
            minWidth: 100,
            suppressHeaderMenuButton: true,
            cellStyle: { display: 'flex', alignItems: 'center' },
        };
    }, []);

    const processCellCallback = (params) => {
        let value = params.value;

        if (typeof value === 'string') {
            value = value.replace(/\\x09|\\x0D/g, '');
        }
        if (typeof value === 'string' && /^[=+\-@]/.test(value[0])) {
            return `'${value}`;
        }

        return value;
    };

    // Memoize the callback for stability if it's used in other hooks/props
    const memoizedProcessCellCallback = useCallback(processCellCallback, []);

    const processHeaderCallback = useCallback(
        (params) => {
            // Get the column definition
            const colDef = params.column.colDef;

            // First try to get the header from csvHeaders if it exists
            if (
                csvHeaders &&
                typeof colDef.field === 'string' &&
                csvHeaders[colDef.field]
            ) {
                return csvHeaders[colDef.field];
            }

            // Otherwise use the headerName from colDef, or as last resort the field name
            return colDef.headerName || colDef.field || '';
        },
        [csvHeaders],
    );

    const defaultCsvExportParams = useMemo(() => {
        return {
            processCellCallback: memoizedProcessCellCallback,
            processHeaderCallback, // Use our new processHeaderCallback
            fileName: titleName ? `${titleName}.csv` : 'export.csv',
            skipHeader: false, // Ensure headers are included
            allColumns: false, // Only export visible columns
        };
    }, [titleName, memoizedProcessCellCallback, processHeaderCallback]);

    const handleExportCsv = useCallback(() => {
        if (gridRef.current?.api) {
            gridRef.current.api.exportDataAsCsv(defaultCsvExportParams);
        } else {
            console.warn('AG Grid API not available for CSV export.');
            // Optionally, provide user feedback here, e.g., via a toast notification
        }
    }, [gridRef, defaultCsvExportParams]); // gridRef itself is stable, defaultCsvExportParams is memoized

    const autoSizeStrategy = useMemo(() => {
        return {
            type: 'fitGridWidth',
        };
    }, []);

    const autoGroupColumnDef = useMemo(() => {
        return {
            minWidth: 200,
        };
    }, []);

    return (
        <>
            {addButtonAction && (
                <Button
                    onClick={addButtonAction}
                    data-walkthroughid={addButtonWalkthroughId}>
                    {`+ ${addButtonText}`}
                </Button>
            )}
            {!noSearchBar && (
                <AGGridSearchBar
                    quickFilterText={quickFilterText}
                    setQuickFilterText={setQuickFilterText}
                    refetch={refetch}
                    extraComponents={extraComponents}
                    onExportCsv={handleExportCsv}
                />
            )}
            <AGGridBase
                gridRef={gridRef}
                tableData={tableData}
                addButtonAction={addButtonAction}
                addButtonWalkthroughId={addButtonWalkthroughId}
                addButtonText={addButtonText}
                masterDetail={masterDetail}
                isRowMaster={isRowMaster}
                isLoading={isLoading}
                quickFilterText={quickFilterText}
                pathname={pathname}
                defaultColDef={defaultColDef}
                autoGroupColumnDef={autoGroupColumnDef}
                customCellRenderer={CopyCellRenderer}
                defaultCsvExportParams={defaultCsvExportParams}
                autoSizeStrategy={autoSizeStrategy}
                rowModelType={rowModelType}
                rowSelection={rowSelection}
                onRowClicked={onRowClicked}
                onSelectionChanged={onSelectionChanged}
                overlayNoRowsTemplate={overlayNoRowsTemplate}
                {...props}
            />
        </>
    );
};

export default AGGridMain;
